import { useState, useEffect } from 'react';
import {
  X, Sparkle, PlusIcon, NotebookPen, Star, ChevronDown, ChevronUp, Pen, Trash2, ArrowRight, SquarePen,
  Download, Settings2, Save, Info, Pencil
} from 'lucide-react';
import { getAllTopicsByTemplateId } from "api/topic";
import { getAllCustomTemplates, getAllTemplates, getFullTemplateDetails, saveTemplate, updateTemplate } from 'api/templates';
import { getUserByCogId } from 'api/user';
import { ScaleLoader } from 'react-spinners';
import { ReactComponent as StarsIcon } from '../assets/svgs/stars.svg';
import ReactDOM from "react-dom";
import { useFieldArray, useForm } from 'react-hook-form';
import InputFieldLabel from './input-field-label';
import { InputDropdown } from './InputDropdown';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Modal from './modal';
import SaveConfirmation from 'sections/modal/SaveConfirmation';
import { createFavoriteTemplate, deleteFavoriteTemplate } from 'api/favorite';
import Spinner from './Spinner';
import DeleteConfirmation from 'sections/modal/DeleteConfirmation';
import UpdateConfirmation from 'sections/modal/UpdateConfirmation';
import { createFile, deleteFile, downloadFile, getFiles } from 'api/file';
import ScaleBarrierLoader from './scale-barrier-loader';
import DeleteFileConfirmation from 'sections/modal/DeleteFileConfirmation';
import UseExistingTemplateModal from './useExistingTemplateModal';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableTopicRow from './SortableTopicRow';

const TemplatesSidebar = () => {

  const { control, register, reset, trigger, handleSubmit, getValues, setValue, formState: { errors } }
    = useForm({
      defaultValues: {
        templateName: "",
        description: "",
        rules: "",
        topics: [{
          summarizationType: 'Points'
        }]
      }
    });

  const inputValues = ["Paragraph", "Points"];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "topics"
  });

  const [user, setUser] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewTopics, setPreviewTopics] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showCustomTemplate, setShowCustomTemplate] = useState(false);
  const [noOfTopics, setNoOfTopics] = useState(1);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [loadingAllTemplates, setLoadingAllTemplates] = useState(false);
  const [notShowTemplateDescription, setNotShowTemplateDescription] = useState([]);
  const [loadingFavoriteId, setLoadingFavoriteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [reUploadText, setReuploadText] = useState(null);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(null);
  const [showUseTemplateModal, setShowUseTemplateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [expandAllTopics, setExpandAllTopics] = useState(false);

  // Reset the expand/collapse state whenever the create/edit modal closes.
  useEffect(() => {
    if (!showCustomTemplate) setExpandAllTopics(false);
  }, [showCustomTemplate]);

  // Initialize data
  useEffect(() => {
    const fetchData = async () => {
      setLoadingAllTemplates(true);
      try {
        const mockUser = await getUserByCogId();
        const mockTemplates = await getAllTemplates();

        setUser(mockUser);
        setAllTemplates(mockTemplates);
      } catch (error) {
        console.error("Error fetching topics:", error);
      } finally {
        setLoadingAllTemplates(false);
      }
    };

    fetchData();
  }, []);

  const handleRemoveTopic = (ind) => {
    if (fields.length === 1) {
      toast.error("At least one topic required");
      return;
    }

    remove(ind);
    setNoOfTopics(prev => prev - 1);
  };

  const handleAddTopics = () => {
    if (fields.length >= 10) {
      toast.error("You cannot add more than 10 topics");
      return;
    }

    append({
      name: "",
      summarizationType: "Points",
      prompt: ""
    });
  };

  const sortByFavorite = (templates) => {
    return [...templates].sort((a, b) => {
      if (a.isFavorite === b.isFavorite) return 0;
      return a.isFavorite ? -1 : 1;
    });
  };

  const handlePreviewClick = async (template) => {
    setPreviewTemplate(template);
    setPreviewTopics(template.topics);
  };

  const handlePreviewButtonClick = async () => {
    const isValid = await trigger();

    if (!isValid) {
      return;
    }

    const { templateId, templateName, rules, description, topics } = getValues();

    const formattedTopics = topics.map((topic, ind) => ({
      name: topic.name,
      description: topic.prompt,
      id: ind,
    }))

    const template = {
      id: templateId,
      name: templateName,
      description,
      rules,
      topics: formattedTopics,
    };

    handlePreviewClick(template);
  };

  const { mutate: saveCustomTemplate, isLoading: saveTemplateLoading } = useMutation(saveTemplate, {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Created");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Failed to create template");
    }
  })

  const { mutate: updateCustomTemplate, isLoading: updateTemplateLoading } = useMutation(
    (data) => updateTemplate(data), {
    onSuccess: () => {
      reset({
        templateName: "",
        description: "",
        topics: [{ summarizationType: "Points" }]
      })
      setShowCustomTemplate(false);
      toast.success("Template Updated");
      queryClient.invalidateQueries('customTemplates');
    },
    onError: () => {
      setShowCustomTemplate(false);
      toast.error("Template update failed.")
    }
  })

  const deepTrim = (value) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value.map(item => deepTrim(item));
    }

    if (typeof value === "object" && value !== null) {
      const trimmedObj = {};
      Object.keys(value).forEach(key => {
        trimmedObj[key] = deepTrim(value[key]);
      });
      return trimmedObj;
    }

    return value;
  };

  const handleSaveTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) {
      toast.error("Please fill all required fields before updating");
      return;
    }

    const topicNames = data.topics.map(t => t?.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (topicNames.length !== nameSet.size) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    saveCustomTemplate(trimmedData);
  };

  const handleUpdateTemplate = async (data) => {
    const isValid = await trigger();
    if (!isValid) return;

    const topicNames = data.topics.map(t => t.name?.trim().toLowerCase());
    const nameSet = new Set(topicNames);

    if (nameSet.size !== topicNames.length) {
      toast.error('Topic names must be unique');
      return;
    }

    const topicsWithIndex = data.topics.map((t, i) => ({
      ...t,
      summarizationType: t.summarizationType === "Points" ? "bullet" : t.summarizationType,
      position: i + 1
    }));

    const finalData = {
      ...data,
      topics: topicsWithIndex
    };

    const trimmedData = deepTrim(finalData);
    updateCustomTemplate(trimmedData);
  };

  useEffect(() => {
    if (!updateTemplateLoading)
      setShowDetails(false);
  }, [updateTemplateLoading])

  useEffect(() => {
    if (!saveTemplateLoading)
      setShowDetails(false);
  }, [saveTemplateLoading])

  const getFilteredTemplates = () => {
    if (!allTemplates.length) return [];
    return allTemplates
      .filter(t => t.medicalProfession && t.medicalProfession.trim() !== '')
      .sort((a, b) =>
        a.medicalProfession.localeCompare(b.medicalProfession) ||
        a.name.localeCompare(b.name)
      );
  };

  const { data: customTemplatesData = [], isLoading: customTemplatesLoading } = useQuery(
    ['customTemplates'], getAllCustomTemplates,
  );

  const closeModal = () => {
    setShowCustomTemplate(false);
    reset({
      templateName: "",
      description: "",
      topics: [
        { summarizationType: "Points", }
      ]
    })
    setIsUpdating(false);
    setNoOfTopics(1);
    setShowDetails(false);
  }

  const sortTopicsByPosition = (topics) => {
    if (!topics) return [];
    return [...topics].sort((a, b) => (a.position) - (b.position));
  };

  const customTemplateUpdation = async (e, templateId) => {
    e.stopPropagation();

    const templateData = await getFullTemplateDetails(templateId);

    const activeTopics = (templateData.topics || []).filter(topic => !topic.isDeleted);

    setNoOfTopics(activeTopics.length)
    reset({
      templateId: templateData.id,
      templateName: templateData.name,
      description: templateData.description,
      rules: templateData.rules,
      topics: sortTopicsByPosition(activeTopics).map(topic => ({
        id: topic.id,
        name: topic.name,
        summarizationType: topic.type === "bullet" ? "Points" : topic.type,
        prompt: topic.description,
      }))
    })
    // Close the "Use Existing" chooser (if open) so the editor isn't hidden behind it.
    setShowUseTemplateModal(false);
    setShowCustomTemplate(true);
  }

  const { mutateAsync: createFavTemplate } = useMutation(
    (data) => createFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Added to favorite templates');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const { mutateAsync: deleteFavTemplate } = useMutation(
    (data) => deleteFavoriteTemplate(data), {
    onSuccess: () => {
      toast.success('Removed from favorite templates');
    },
    onError: () => {
      toast.error("Something went wrong");
    }
  })

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setIsPreviewLoading(true);

    try {
      const topicsByTemplate = await getAllTopicsByTemplateId(template.id);
      const activeTopics = (topicsByTemplate || []).filter(topic => !topic.isDeleted);
      setPreviewTopics(sortTopicsByPosition(activeTopics));
    } catch (error) {
      console.error('Error fetching topics:', error);
      setPreviewTopics([]);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setNotShowTemplateDescription([]);
    setPreviewTemplate(null);
    setPreviewTopics([]);
  };

  const queryClient = useQueryClient();

  const toggleFavoriteCustomTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;
    setLoadingFavoriteId(template.id);

    try {
      if (!isFavorite)
        await createFavTemplate({ templateId: template.id })
      else
        await deleteFavTemplate({ templateId: template.id })

      queryClient.setQueriesData(['customTemplates'], (oldData = []) => {
        return oldData.map((t) => {
          if (t.id === template.id) {
            return {
              ...t,
              isFavorite: !t.isFavorite
            }
          }
          else
            return t;
        })
      })

    } catch (error) {
      toast.error(error.message);
      console.log(error);
    } finally {
      setLoadingFavoriteId(null);
    }
  }

  const toggleFavoriteTemplate = async (e, template) => {
    e.stopPropagation();
    const isFavorite = template.isFavorite;
    setLoadingFavoriteId(template.id);
    try {
      if (!isFavorite)
        await createFavTemplate({ templateId: template.id });
      else
        await deleteFavTemplate({ templateId: template.id });

      setAllTemplates(prev =>
        prev.map(t =>
          t.id === template.id
            ? { ...t, isFavorite: !t.isFavorite }
            : t
        )
      );

    } catch (error) {
      toast.error(error.message);
      console.log(error);
    } finally {
      setLoadingFavoriteId(null);
    }
  }

  const FavoriteButton = ({ template, isFavorite }) => {
    return <button
      disabled={loadingFavoriteId === template.id}
      onClick={(e) => {
        e.stopPropagation();
        template.userId === null
          ? toggleFavoriteTemplate(e, template)
          : toggleFavoriteCustomTemplate(e, template);
      }}
      className="cursor-pointer transition-transform duration-200 hover:scale-110"
    >
      {loadingFavoriteId === template.id ? (
        <Spinner size={18} />
      ) : (
        <Star
          className="transition-all duration-200"
          size={20}
          fill={isFavorite ? '#fbbf24' : 'none'}
          stroke="currentColor"
          strokeWidth={2}
        />
      )}
    </button>
  }

  const { mutate: createFileMutation } = useMutation(createFile,
    {
      onSuccess: () => {
        toast.success("File uploaded successfully!");
      },
      onError: () => {
        toast.error("File Upload Failed!");
      },
      onSettled: () => {
        queryClient.invalidateQueries('getUserFiles');
        setReuploadText('');
      }
    });

  const { data: myDocs } = useQuery('getUserFiles', getFiles);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);

    const newArr = arrayMove(getValues("topics"), oldIndex, newIndex);
    setValue("topics", newArr);
  };

  const TemplateCard = ({ template }) => {
    const isFavorite = template.isFavorite;
    const isCustomTemplate = template.userId;

    return (
      <div className="group flex flex-col justify-between gap-3 bg-white rounded-xl border border-gray-300 p-5 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-300 transform hover:-translate-y-1">
        
        <div className='flex justify-between items-start'>
          <div className='flex items-center gap-2'>
            <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
              <NotebookPen className="h-5 w-5 text-blue-600" />
            </div>
            {isFavorite && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <Star className="h-3 w-3 mr-1" fill="#fbbf24" />
                Favorite
              </span>
            )}
          </div>

          <div className='flex gap-2'>
            {!isCustomTemplate && <FavoriteButton template={template} isFavorite={isFavorite} />}
            
            {isCustomTemplate && (
              <>
                <button
                  onClick={(e) => {
                    setIsUpdating(true);
                    customTemplateUpdation(e, template.id);
                  }}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                  title="Edit template"
                >
                  <Pen size={18} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteModal({
                      templateId: template.id,
                      templateName: template.name,
                    });
                  }}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                  title="Delete template"
                >
                  <Trash2 size={18} />
                </button>
                <FavoriteButton template={template} isFavorite={isFavorite} />
              </>
            )}
          </div>
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-base mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors">
            {template.name}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
            {template.description}
          </p>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          <button
            onClick={() => handlePreview(template)}
            className="inline-flex bg-primary items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 transform hover:scale-105"
          >
            <Sparkle size={14} />
            Preview
          </button>

          {!isCustomTemplate && (
            <button
              type="button"
              onClick={(e) => customTemplateUpdation(e, template.id)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
            >
              Customise
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const TabButton = ({ tabId, label, isActive, onClick, noRounded = false }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 whitespace-nowrap transform hover:scale-105 ${
        isActive
          ? 'bg-primary text-black shadow-md'
          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
        }
        ${noRounded ? 'rounded-lg' : 'rounded-full'}
        `}
    >
      {label}
    </button>
  );

  const nirvaAndCustomTemplates = [...allTemplates, ...customTemplatesData];

  const filteredAllTemplates = nirvaAndCustomTemplates.filter(({ name }) =>
    name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const CreateTemplateCard = () => (
    <div className='group bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col gap-3 rounded-xl p-5 border-2 border-dashed border-purple-300 hover:border-purple-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1'>
      
      <div className='p-3 bg-white rounded-full w-fit shadow-sm group-hover:shadow-md transition-shadow'>
        <PlusIcon className="h-6 w-6 text-purple-600" strokeWidth={3} />
      </div>

      <div className='font-semibold text-lg text-gray-900'>
        Create Custom Template
      </div>

      <p className='text-sm text-gray-600 line-clamp-3 leading-relaxed'>
        Design your own template with custom topics, formatting, and structure tailored to your practice.
      </p>

      <div className='flex gap-2 items-center flex-wrap mt-2'>
        <button
          type="button"
          onClick={() => setShowCustomTemplate(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
        >
          <Pencil className='w-4 h-4'/>
          Create New
        </button>

        <button
          type="button"
          onClick={() => setShowUseTemplateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
        >
          <SquarePen size={16} />
          Use Existing
        </button>
      </div>
    </div>
  )

  const UploadFormCard = () => (
    <label htmlFor='upload-file'>
      <div className='group bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col gap-3 rounded-xl p-5 border-2 border-dashed border-emerald-300 hover:border-emerald-400 cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1'>
        
        <div className='p-3 bg-white rounded-full w-fit shadow-sm group-hover:shadow-md transition-shadow'>
          <PlusIcon className="h-6 w-6 text-emerald-600" strokeWidth={3} />
        </div>

        <div className='font-semibold text-lg text-gray-900'>
          Upload Form
        </div>
        
        <p className='text-sm text-gray-600 line-clamp-3 leading-relaxed'>
          Pdf form to be filled
        </p>

        <div className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg shadow-md hover:shadow-lg w-fit mt-2">
          <StarsIcon className='w-4 h-4' />
          Create
        </div>
      </div>
    </label>
  )

  const handleDownload = async (docId) => {
    const fileData = await downloadFile(docId);
    const link = document.createElement("a");
    link.href = fileData.url;
    link.download = fileData.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const MyDocCard = ({ doc }) => {
    return (
      <div className="group flex flex-col justify-between gap-3 bg-white rounded-xl border border-gray-300 p-5 shadow-sm hover:shadow-lg hover:border-amber-300 transition-all duration-300 transform hover:-translate-y-1">
        
        <div className='w-full flex justify-between items-start'>
          <div className="p-2 bg-amber-50 rounded-lg group-hover:bg-amber-100 transition-colors">
            <NotebookPen className="h-5 w-5 text-amber-600" />
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteFileModal({
                docId: doc.id,
                docName: doc.fileName,
              })
            }}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
          >
            <Trash2 size={18} />
          </button>
        </div>

        <h3 className="font-semibold text-gray-900 text-base line-clamp-2">
          {doc.fileName}
        </h3>

        <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
          {doc.description}
        </p>

        <div className='flex justify-end pt-2 border-t border-gray-100'>
          <button
            onClick={() => handleDownload(doc.id)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
          >
            <Download size={20} />
          </button>
        </div>
      </div>
    );
  };

  const filteredTemplates = getFilteredTemplates();

  return (
    <div className="w-full overflow-y-auto mb-4">
      
      {/* Tab Navigation */}
      <div className="mb-8 bg-white rounded-lg border border-gray-300 p-4 shadow-sm">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <TabButton
              noRounded={true}
              tabId="nirva"
              label="Nirva Templates"
              isActive={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
            />
            <TabButton
              noRounded={true}
              tabId="custom"
              label="My Templates"
              isActive={activeTab === 'custom'}
              onClick={() => setActiveTab('custom')}
            />
            {/* TODO(FILL_A_DOC): Intentionally disabled. Uncomment to re-enable. */}
            {/* <TabButton
              noRounded={true}
              tabId="myDocs"
              label="My Documents"
              isActive={activeTab === 'myDocs'}
              onClick={() => setActiveTab('myDocs')}
            /> */}
          </div>
          
          <div className='flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg'>
            <Star fill="#fbbf24" className="text-yellow-600 w-5 h-5 flex-shrink-0" strokeWidth={2} />
            <p className='text-sm text-gray-700'>
              <span className="font-medium">Favorite templates</span> will be auto-generated during consultation sessions
            </p>
          </div>
        </div>

        <input
          id="upload-file"
          accept={process.env.REACT_APP_ACCEPT_DOCs}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > process.env.REACT_APP_MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
              toast.error("File size must be under 10 MB");
              return;
            }
            setReuploadText('File Uploading...');
            createFileMutation(file);
          }}
        />
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'custom' ? (
          customTemplatesLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <CreateTemplateCard />
              {sortByFavorite(customTemplatesData).map((template, index) => (
                <div
                  key={template.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <TemplateCard template={template} />
                </div>
              ))}
            </div>
          )
        ) : activeTab === 'myDocs' ? (
          customTemplatesLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-emerald-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <UploadFormCard />
              {myDocs?.map((doc, index) => (
                <div
                  key={doc.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <MyDocCard doc={doc} />
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loadingAllTemplates ? (
              <div className="col-span-full flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600"></div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <NotebookPen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 text-base">No templates found for this category.</p>
              </div>
            ) : (
              sortByFavorite(filteredTemplates).map((template, index) => (
                <div
                  key={template.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <TemplateCard template={template} />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showUseTemplateModal && (
        <UseExistingTemplateModal
          setShowUseTemplateModal={setShowUseTemplateModal}
          filteredAllTemplates={filteredAllTemplates}
          handlePreviewClick={handlePreview}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          customTemplateUpdation={customTemplateUpdation}
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[92] p-4 animate-fade-in">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-2xl animate-scale-in">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkle className="h-5 w-5 text-blue-600" />
                  {previewTemplate.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Template Preview
                </p>
              </div>
              <button
                onClick={closePreview}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 transform hover:rotate-90"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="mb-6">
                <p className="text-black text-sm leading-relaxed">
                  {previewTemplate.description}
                </p>

                {/*{previewTemplate.rules && previewTemplate.rules.length !== 0 && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-black text-sm mb-2 flex items-center gap-2">
                      <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                      Template Rules
                    </h4>
                    <p className="text-black text-sm leading-relaxed">
                      {previewTemplate.rules}
                    </p>
                  </div>
                )}*/}
              </div>

              {/* Topics */}
              <div>
                <h4 className="font-semibold text-black text-base mb-3 flex items-center gap-2">
                  <div className="w-1 h-5 bg-purple-600 rounded-full"></div>
                  Template Topics
                </h4>
                <p className="text-black text-sm mb-4">
                  Consultations are categorized and summarized under these topics.
                </p>
                
                {isPreviewLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {previewTopics.map((topic, index) => (
                      <div
                        key={index}
                        className='bg-white border border-gray-400 rounded-lg hover:border-blue-300 transition-all duration-200 overflow-hidden'
                      >
                        <button
                          onClick={() => setNotShowTemplateDescription(prev => {
                            let newIds = [...prev];
                            if (!newIds.includes(topic.id)) {
                              newIds.push(topic.id);
                            } else {
                              newIds = newIds.filter(newTopicId => newTopicId !== topic.id)
                            }
                            return newIds;
                          })}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <h5 className="font-medium text-black text-sm text-left">
                            {topic.name || 'Untitled Topic'}
                          </h5>
                          {notShowTemplateDescription.includes(topic.id) ? 
                            <ChevronDown className="h-5 w-5 text-black" /> : 
                            <ChevronUp className="h-5 w-5 text-black" />
                          }
                        </button>

                        {!notShowTemplateDescription.includes(topic.id) && (
                          <div className='border-t border-gray-200 p-4 bg-gray-50 text-sm text-black leading-relaxed animate-slide-down'>
                            {topic.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closePreview}
                className="px-4 py-2 text-sm font-medium text-black bg-red3 hover:bg-rose-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showCustomTemplate && (
        <>
          {ReactDOM.createPortal(
            <div className="fixed inset-0 z-[89] flex items-center justify-center p-4 animate-fade-in">
              <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={closeModal}
              />
              <div
                className="relative w-[90vw] sm:w-[80vw] lg:w-[70vw] h-[90vh] max-w-[1000px] rounded-xl bg-white shadow-2xl p-6 flex flex-col border border-gray-200 z-[91] animate-scale-in"
              >
              <div className="relative w-full flex flex-col h-full">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                  <h2 className="font-bold text-2xl text-gray-900">
                    {!isUpdating ? 'Create Custom Template' : 'Update Template'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex flex-col">
                    <InputFieldLabel isRequired={true} label="Template Name" className="mb-1.5 text-sm font-medium" />
                    <input
                      {...register("templateName", { required: "Template name is required" })}
                      placeholder="Enter template name..."
                      className="py-2 px-4 input-field-classes text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors?.templateName && (
                      <span className="text-red-600 text-sm mt-1">{errors.templateName.message}</span>
                    )}
                  </div>

                  {showDetails && (
                    <>
                      <div className="flex flex-col">
                        <InputFieldLabel label="Description" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("description")}
                          className="py-2 px-4 input-field-classes border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Describe your template..."
                        />
                      </div>

                      <div className="flex flex-col">
                        <InputFieldLabel label="Rules" className="mb-1.5 text-sm font-medium" />
                        <textarea
                          {...register("rules")}
                          className="py-2 px-4 input-field-classes border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows={3}
                          placeholder="Add any specific rules or guidelines..."
                        />
                      </div>
                    </>
                  )}

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium self-start"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp size={16} />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Show Details
                      </>
                    )}
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Topics</h3>
                    <button
                      type="button"
                      onClick={() => setExpandAllTopics(prev => !prev)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      {expandAllTopics ? (
                        <>
                          <ChevronUp size={16} />
                          Collapse all
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Expand all
                        </>
                      )}
                    </button>
                  </div>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {fields.map((field, ind) => (
                        <SortableTopicRow
                          key={field.id}
                          field={field}
                          ind={ind}
                          register={register}
                          errors={errors}
                          getValues={getValues}
                          setValue={setValue}
                          fields={fields}
                          inputValues={inputValues}
                          handleRemoveTopic={handleRemoveTopic}
                          openDropdownIndex={openDropdownIndex}
                          setOpenDropdownIndex={setOpenDropdownIndex}
                          expanded={expandAllTopics}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {fields.length < 10 && (
                    <button
                      type="button"
                      onClick={handleAddTopics}
                      className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                    >
                      <PlusIcon className="h-5 w-5 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Add Topic</span>
                    </button>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={handlePreviewButtonClick}
                    disabled={saveTemplateLoading || updateTemplateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-black bg-primary hover:bg-primary rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
                  >
                    <Sparkle size={16} />
                    Preview
                  </button>

                  {!isUpdating ? (
                    <button
                      onClick={handleSubmit(handleSaveTemplate)}
                      disabled={saveTemplateLoading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-black bg-green hover:bg-lime-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saveTemplateLoading ? 'Saving...' : 'Save Template'}
                    </button>
                  ) : (
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-black bg-green hover:bg-lime-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSubmit(handleUpdateTemplate)}
                      disabled={updateTemplateLoading}
                    >
                      {updateTemplateLoading ? 'Updating...' : 'Update Template'}
                    </button>
                  )}
                </div>
              </div>
              </div>
            </div>,
            document.body
          )}
        </>
      )}

      {showDeleteFileModal && (
        <Modal
          isOpen={showDeleteFileModal}
          closeModal={() => setShowDeleteFileModal(false)}
        >
          <DeleteFileConfirmation
            data={showDeleteFileModal}
            setShowDeleteFileModal={setShowDeleteFileModal}
          />
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
        >
          <DeleteConfirmation
            data={showDeleteModal}
            setShowDeleteModal={() => setShowDeleteModal(false)}
          />
        </Modal>
      )}

      {reUploadText && <ScaleBarrierLoader text={reUploadText} />}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default TemplatesSidebar;